import React, { useState, useEffect, useRef } from "react";
import {
  IconMoodSmile,
  IconPaperclip,
  IconLink,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { FieldError, RegisterOptions, UseFormRegister } from "react-hook-form";
import FormErrorMessage from "./FormErrorMessage";
import { useLanguage } from "../../context/LanguageContext";

type TextareaFieldProps = {
  label?: string;
  title?: string;
  placeholder?: string;
  className?: string;
  classLabel?: string;
  name: string;
  rows?: number;
  maxLength?: number;
  register?: UseFormRegister<any>;
  validation?: RegisterOptions;
  disabled?: boolean;
  value?: string;
  error?: FieldError | any;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleFileClick?: (e: any) => void;
  fileName?: any;
  fileInputRef?: any;
  inputClassName?: string;
  handleFileChange?: (e: any) => void;
  showToolbar?: boolean;
  onEmojiClick?: () => void;
  onAttachClick?: () => void;
  onLinkClick?: () => void;
};

const POPULAR_EMOJIS = [
  "😊", "😂", "🥰", "😍", "👍", "🙏", "🔥", "🎉",
  "✨", "🚀", "💯", "❤️", "⭐", "👏", "💡", "📦",
  "📌", "✅", "⚠️", "🎯"
];

const TextareaField = ({
  label,
  title,
  placeholder = "Share your thoughts...",
  className = "",
  classLabel = "",
  rows = 4,
  maxLength = 150,
  value,
  name,
  register,
  validation,
  disabled = false,
  error,
  onChange,
  handleFileClick,
  handleFileChange,
  fileInputRef,
  inputClassName,
  fileName,
  showToolbar = true,
  onEmojiClick,
  onAttachClick,
  onLinkClick,
}: TextareaFieldProps) => {
  const { t } = useLanguage();
  const [isFocused, setIsFocused] = useState(false);
  const [internalValue, setInternalValue] = useState(value || "");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const localFileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (value !== undefined) setInternalValue(value);
  }, [value]);

  const registeredProps = register ? register(name, validation) : null;
  const { ref: registeredRef, ...restRegisteredProps } = (registeredProps || {}) as any;

  const triggerChange = (newValue: string) => {
    if (textareaRef.current) {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype,
        "value"
      )?.set;
      nativeInputValueSetter?.call(textareaRef.current, newValue);

      const event = new Event("input", { bubbles: true });
      textareaRef.current.dispatchEvent(event);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInternalValue(e.target.value);
    if (restRegisteredProps?.onChange) restRegisteredProps.onChange(e);
    if (onChange) onChange(e);
  };

  const insertTextAtCursor = (textToInsert: string) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      const newValue = internalValue + textToInsert;
      setInternalValue(newValue);
      triggerChange(newValue);
      return;
    }
    const start = textarea.selectionStart || 0;
    const end = textarea.selectionEnd || 0;
    const newValue =
      internalValue.substring(0, start) +
      textToInsert +
      internalValue.substring(end);

    setInternalValue(newValue);
    triggerChange(newValue);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + textToInsert.length,
        start + textToInsert.length
      );
    }, 0);
  };

  const handleSelectEmoji = (emoji: string) => {
    insertTextAtCursor(emoji);
    setShowEmojiPicker(false);
    if (onEmojiClick) onEmojiClick();
  };

  const handleAddLink = (e?: any) => {
    if (e) {
      if (typeof e.preventDefault === "function") e.preventDefault();
      if (typeof e.stopPropagation === "function") e.stopPropagation();
    }
    if (linkUrl.trim()) {
      const formattedLink = linkUrl.startsWith("http")
        ? linkUrl
        : `https://${linkUrl}`;
      insertTextAtCursor(` ${formattedLink} `);
      setLinkUrl("");
      setShowLinkInput(false);
      if (onLinkClick) onLinkClick();
    }
  };

  const currentLength =
    typeof internalValue === "string" ? internalValue.length : 0;
  const isLimitReached = currentLength >= maxLength;
  const labelText = label || title || placeholder;
  const isFloating =
    isFocused || Boolean(internalValue && internalValue !== "");

  return (
    <div className={`w-full relative pt-1.5 ${className}`}>
      {/* Fallback File Input if not passed */}
      <input
        type="file"
        ref={fileInputRef || localFileInputRef}
        className="hidden"
        accept="image/*"
        onChange={handleFileChange}
      />

      {name === "notificationMessage" && (
        <div className="flex justify-end mb-1">
          <button
            type="button"
            onClick={handleFileClick}
            title={fileName || "Choose Img"}
            className="inline-flex items-center gap-1 rounded-xl border border-[#FBD037] bg-[#FBD037]/20 hover:bg-[#FBD037]/40 px-1 py-1 cursor-pointer transition-all max-w-[170px]"
          >
            <IconUpload stroke={1.5} size={14} className="shrink-0 text-[#1E293B]" />
            <span className="text-xs font-semibold text-[#1E293B] truncate max-w-[130px] block">
              {fileName || "Choose Img"}
            </span>
          </button>
        </div>
      )}

      {/* Textarea Box Container */}
      <div
        className={`textarea-box relative w-full rounded-[18px] transition-all duration-200 ${
          error
            ? "border-2 border-red-500 bg-red-50/40"
            : isFocused
            ? "border-2 border-[#FBD037] bg-yellow-50/70 ring-2 ring-[#FBD037]/30 shadow-sm"
            : "border-2 border-[#FBD037]/60 bg-yellow-50/40 hover:border-[#FBD037]"
        } ${disabled ? "opacity-60 bg-gray-100 cursor-not-allowed" : ""}`}
      >
        <textarea
          ref={(e) => {
            textareaRef.current = e;
            if (registeredRef) {
              if (typeof registeredRef === "function") {
                registeredRef(e);
              } else {
                (registeredRef as any).current = e;
              }
            }
          }}
          id={name}
          placeholder={isFloating ? t(placeholder) : " "}
          rows={rows}
          maxLength={maxLength}
          {...restRegisteredProps}
          value={internalValue}
          disabled={disabled}
          onFocus={(e) => {
            setIsFocused(true);
            if (restRegisteredProps?.onBlur) restRegisteredProps.onBlur(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            if (restRegisteredProps?.onBlur) restRegisteredProps.onBlur(e);
          }}
          onChange={handleChange}
          className={`peer w-full rounded-[18px] pt-4 pb-12 px-4 outline-none text-xs text-[#1E293B] bg-transparent resize-none leading-relaxed transition-all ${
            inputClassName || ""
          } ${disabled ? "cursor-not-allowed text-gray-500" : ""}`}
        />

        {/* Floating Label */}
        {labelText && (
          <label
            className={`absolute pointer-events-none transition-all duration-200 ease-in-out select-none rounded-md ${
              isFloating
                ? "-top-2.5 left-3 text-[11px] font-semibold bg-yellow-50 z-10 px-1.5"
                : "left-4 top-3 text-xs bg-transparent z-0 px-1"
            } ${
              error ? "!text-red-500" : "!text-black font-medium"
            } ${disabled ? "!bg-gray-100 !text-gray-400" : ""} ${classLabel}`}
          >
            {t(labelText)}
            {validation?.required && (
              <span className="text-red-500 ml-0.5">*</span>
            )}
          </label>
        )}

        {/* Toolbar with action buttons & character counter ALIGNED TO RIGHT */}
        {showToolbar && (
          <div className="toolbar absolute bottom-2.5 right-3.5 flex items-center justify-end gap-2 pointer-events-auto z-20">
            {/* Emoji Button */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setShowEmojiPicker(!showEmojiPicker);
                  setShowLinkInput(false);
                }}
                disabled={disabled}
                className="emoji w-8 h-8 rounded-xl bg-[#FBD037]/20 text-[#1E293B] hover:bg-[#FBD037] hover:text-black flex items-center justify-center transition-all cursor-pointer disabled:cursor-not-allowed shadow-sm"
                title="Emoji"
              >
                <IconMoodSmile size={18} stroke={1.8} />
              </button>

              {/* Emoji Picker Popover */}
              {showEmojiPicker && (
                <div className="absolute bottom-10 right-0 z-50 bg-white rounded-2xl p-2.5 shadow-xl border border-yellow-200 grid grid-cols-5 gap-1.5 w-48">
                  {POPULAR_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => handleSelectEmoji(emoji)}
                      className="w-8 h-8 flex items-center justify-center text-base rounded-lg hover:bg-yellow-100 transition-colors cursor-pointer"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Attach Button */}
            <button
              type="button"
              onClick={() => {
                if (onAttachClick) {
                  onAttachClick();
                } else if (handleFileClick) {
                  handleFileClick(null);
                } else if (localFileInputRef.current) {
                  localFileInputRef.current.click();
                }
              }}
              disabled={disabled}
              className="attach w-8 h-8 rounded-xl bg-[#FBD037]/20 text-[#1E293B] hover:bg-[#FBD037] hover:text-black flex items-center justify-center transition-all cursor-pointer disabled:cursor-not-allowed shadow-sm"
              title="Attach File"
            >
              <IconPaperclip size={18} stroke={1.8} />
            </button>

            {/* Link Button */}
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  setShowLinkInput(!showLinkInput);
                  setShowEmojiPicker(false);
                }}
                disabled={disabled}
                className="link w-8 h-8 rounded-xl bg-[#FBD037]/20 text-[#1E293B] hover:bg-[#FBD037] hover:text-black flex items-center justify-center transition-all cursor-pointer disabled:cursor-not-allowed shadow-sm"
                title="Insert Link"
              >
                <IconLink size={18} stroke={1.8} />
              </button>

              {/* Link Input Popover */}
              {showLinkInput && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="absolute bottom-11 right-0 z-50 bg-[#FDFDF6] rounded-2xl p-2 shadow-2xl border-2 border-[#FBD037] flex items-center gap-1.5 w-64 animate-in fade-in slide-in-from-bottom-2 duration-150"
                >
                  <div className="flex-1 flex items-center gap-1.5 bg-yellow-50/80 border border-[#FBD037]/60 rounded-xl px-2.5 py-1.5 transition-all focus-within:border-[#FBD037] focus-within:ring-2 focus-within:ring-[#FBD037]/30">
                    <IconLink size={14} className="text-[#1E293B] shrink-0 opacity-60" />
                    <input
                      type="text"
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAddLink(e);
                        }
                      }}
                      placeholder="https://example.com"
                      className="w-full text-xs text-[#1E293B] bg-transparent outline-none placeholder:text-slate-400"
                      autoFocus
                    />
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAddLink(e);
                    }}
                    className="px-3 py-1.5 bg-[#FBD037] hover:bg-[#e0b82e] text-[#1E293B] text-xs font-bold rounded-xl shadow-xs transition-all cursor-pointer shrink-0"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowLinkInput(false);
                    }}
                    className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200/50 rounded-lg transition-all cursor-pointer shrink-0"
                  >
                    <IconX size={14} />
                  </button>
                </div>
              )}
            </div>

            {/* Counter Pill */}
            <span
              className={`counter px-2.5 py-1 text-xs font-semibold rounded-full transition-all shadow-sm ${
                isLimitReached
                  ? "limit bg-[#FFF1F1] text-[#EF4444] border border-red-200"
                  : "bg-[#FBD037]/20 text-[#1E293B]"
              }`}
            >
              <span id="count">{currentLength}</span>/{maxLength}
            </span>
          </div>
        )}
      </div>

      <FormErrorMessage error={error} />
    </div>
  );
};

export default TextareaField;
